import { Container } from '@/components';
import ScrollableSelect from '@/components/select/ScrollableSelect';
import { Toolbar, ToolbarHeading } from '@/layouts/demo1/toolbar';
import {
  useExportKpiLogsCsvMutation,
  useExportKpiLogsJsonMutation,
  useGetEngagementMetricsMutation,
} from '@/services/KpiService';
import { ExportKpiLogsDto, KpiMetricsVm } from '@/types/kpi.types';
import React, { useState, useEffect } from 'react';
import { KpiLogsTable } from './KpiLogsTable';
import { format } from 'date-fns';
import { KpiType, UserRoles } from '@/constant';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { formatFinnishNumber, formatFinnishPercentage } from '@/utils';

interface KpiFilters {
  startDate: Date;
  endDate: Date;
  kpiType: string;
  role: string;
}

type KpiFilterKey = keyof KpiFilters;

interface KpiMetricsProps {
  metrics?: KpiMetricsVm;
}

const KpiMetrics: React.FC<KpiMetricsProps> = ({ metrics: initialMetrics }) => {
  const [getEngagementMetrics] = useGetEngagementMetricsMutation();
  const [exportKpiLogsJson] = useExportKpiLogsJsonMutation();
  const [exportKpiLogsCsv] = useExportKpiLogsCsvMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [metrics, setMetrics] = useState<KpiMetricsVm | undefined>(
    initialMetrics
  );
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState<'json' | 'csv' | null>(
    null
  );
  const [filters, setFilters] = useState<KpiFilters>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    kpiType: '',
    role: '',
  });

  const handleFilterChange = <K extends KpiFilterKey>(
    name: K,
    value: KpiFilters[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const KpiTypeArray = [
    { value: '1', label: 'Route' },
    { value: '2', label: 'POI' },
    { value: '3', label: 'Booking' },
  ] as const;

  const userRoles = [
    { value: '', label: 'All Roles' },
    { value: UserRoles.ADMIN, label: 'Admin' },
    { value: UserRoles.YACHT_CLUB_ADMIN, label: 'Yacht Club Admin' },
    { value: UserRoles.REGIONAL_OPERATOR, label: 'Regional Operator' },
    { value: UserRoles.DMO, label: 'DMO' },
    { value: UserRoles.BOATER, label: 'Boater' },
    { value: UserRoles.MARINA, label: 'Marina' },
  ] as const;

  const formatMetricCount = (value: number | string | undefined) =>
    formatFinnishNumber(value ?? 0, { maximumFractionDigits: 0 });

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const { startDate, endDate, kpiType, role } = filters;
        const response = await getEngagementMetrics({
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(kpiType && { kpiType }),
          ...(role && { role: role as UserRoles }),
        }).unwrap();
        setMetrics(response);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [filters, getEngagementMetrics, initialMetrics]);

  const handleExport = async (format: 'json' | 'csv') => {
    setExportLoading(format);
    try {
      const { startDate, endDate, kpiType, role } = filters;
      const dto: ExportKpiLogsDto = {
        format,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(kpiType && { kpiType: parseInt(kpiType) }),
        ...(role && { role: role as UserRoles }),
      };

      let response;
      let blob: Blob | null = null;
      let filename = '';
      const dateRange = `${filters.startDate.toISOString().split('T')[0]}_to_${filters.endDate.toISOString().split('T')[0]}`;

      if (format === 'json') {
        response = await exportKpiLogsJson(dto).unwrap();

        if (response.format === 'json') {
          if (Array.isArray(response.data)) {
            blob = new Blob([JSON.stringify(response.data, null, 2)], {
              type: 'application/json',
            });
            filename = `kpi-logs-${dateRange}.json`;
          } else if (
            response.data &&
            typeof response.data === 'object' &&
            'message' in response.data
          ) {
            const errorMessage = response.data.message || 'No data available';
            enqueueSnackbar(errorMessage, { variant: 'warning' });
            throw new Error(errorMessage);
          } else {
            const errorMessage = 'Invalid data format received from server';
            enqueueSnackbar(errorMessage, { variant: 'error' });
            throw new Error(errorMessage);
          }
        }
      } else {
        response = await exportKpiLogsCsv(dto).unwrap();
        blob = new Blob([response], { type: 'text/csv' });
        filename = `kpi-logs-${dateRange}.csv`;
      }

      if (!blob) {
        throw new Error('No data available to export');
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Failed to export ${format} logs:`, error);
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading
            id="kpi-management-heading"
            title="KPI Management"
            description="Monitor and Analyze Key Performance Indicators"
          />
        </Toolbar>
        <div className=" min-h-screen">
          {/* Header Section */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold text-gray-800">
                  Track Engagement Metrics
                </h1>
              </div>
              <div className="space-x-4">
                {hasPermission('kpi_management_read') && (
                  <button
                    onClick={() => handleExport('json')}
                    className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 ${exportLoading === 'json' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={exportLoading === 'json'}
                  >
                    {exportLoading === 'json' ? 'Exporting...' : 'Export JSON'}
                  </button>
                )}
                {hasPermission('kpi_management_read') && (
                  <button
                    onClick={() => handleExport('csv')}
                    className={`px-4 py-2 bg-green-600 contrast:bg-green-800 text-white rounded-lg hover:bg-green-700 transition duration-200 ${exportLoading === 'csv' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={exportLoading === 'csv'}
                  >
                    {exportLoading === 'csv' ? 'Exporting...' : 'Export CSV'}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label
                  htmlFor="start-date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Start Date
                </label>
                <input
                  id="start-date"
                  data-testid="start-date-input"
                  type="date"
                  autoComplete="off"
                  value={format(filters.startDate, 'yyyy-MM-dd')}
                  onChange={(e) =>
                    handleFilterChange('startDate', new Date(e.target.value))
                  }
                  className="w-full p-2 border rounded-md"
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label
                  htmlFor="end-date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  End Date
                </label>
                <input
                  id="end-date"
                  data-testid="end-date-input"
                  type="date"
                  autoComplete="off"
                  value={format(filters.endDate, 'yyyy-MM-dd')}
                  onChange={(e) =>
                    handleFilterChange('endDate', new Date(e.target.value))
                  }
                  className="w-full p-2 border rounded-md"
                  min={format(filters.startDate, 'yyyy-MM-dd')}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label
                  htmlFor="content-type"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content Type
                </label>
                <ScrollableSelect
                  id="content-type"
                  data-testid="content-type-select"
                  value={filters.kpiType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange('kpiType', e.target.value)
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">All Types</option>
                  {KpiTypeArray.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </ScrollableSelect>
              </div>

              <div>
                <label
                  htmlFor="user-role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  User Role
                </label>
                <ScrollableSelect
                  id="user-role"
                  data-testid="user-role-select"
                  value={filters.role}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange('role', e.target.value)
                  }
                  className="w-full p-2 border rounded-md"
                >
                  {userRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </ScrollableSelect>
              </div>
            </div>
          </div>

          {/* Metrics Overview */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div
                data-testid="loading-spinner"
                className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"
              ></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Bounce Rate Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Bounce Rate (%)
                </h2>
                <div className="space-y-4">
                  {KpiTypeArray.map((type) => (
                    <div key={type.value} className="flex justify-between">
                      <span className="text-gray-600">{type.label}</span>
                      <span className="font-bold text-gray-900">
                        {formatFinnishPercentage(
                          metrics?.bounceRate[
                            type.label.toUpperCase() as keyof typeof metrics.bounceRate
                          ] || 0,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dwell Time Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Avg Dwell Time (s)
                </h2>
                <div className="space-y-4">
                  {KpiTypeArray.map((type) => (
                    <div key={type.value} className="flex justify-between">
                      <span className="text-gray-600">{type.label}</span>
                      <span className="font-bold text-gray-900">
                        {formatFinnishNumber(
                          metrics?.dwellTime[
                            type.label.toUpperCase() as keyof typeof metrics.dwellTime
                          ] || 0,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking Events Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Booking Events
                </h2>
                <div className="space-y-4">
                  {metrics?.pageViewsByEvent?.BOOKING ? (
                    Object.entries(metrics.pageViewsByEvent.BOOKING).map(
                      ([event, count]) => (
                        <div key={event} className="flex justify-between">
                          <span className="text-gray-600">{event}</span>
                          <span className="font-bold text-gray-900">
                            {formatMetricCount(count)}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-gray-600">No data available</div>
                  )}
                </div>
              </div>
              {/* Route Events Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Route Events
                </h2>
                <div className="space-y-4">
                  {metrics?.pageViewsByEvent?.ROUTE ? (
                    Object.entries(metrics.pageViewsByEvent.ROUTE).map(
                      ([event, count]) => (
                        <div key={event} className="flex justify-between">
                          <span className="text-gray-600">{event}</span>
                          <span className="font-bold text-gray-900">
                            {formatMetricCount(count)}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-gray-600">No data available</div>
                  )}
                </div>
              </div>

              {/* POI Events Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  POI Events
                </h2>
                <div className="space-y-4">
                  {metrics?.pageViewsByEvent?.POI ? (
                    Object.entries(metrics.pageViewsByEvent.POI).map(
                      ([event, count]) => (
                        <div key={event} className="flex justify-between">
                          <span className="text-gray-600">{event}</span>
                          <span className="font-bold text-gray-900">
                            {formatMetricCount(count)}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-gray-600">No data available</div>
                  )}
                </div>
              </div>
              {/* Most Used Feature Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 md:col-span-2 lg:col-span-1">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Most Used Feature
                </h2>
                <p className="text-2xl font-bold text-green-600">
                  {metrics?.mostUsedFeature &&
                  Number(metrics?.mostUsedFeature) === KpiType.ROUTE
                    ? 'Route'
                    : Number(metrics?.mostUsedFeature) === KpiType.POI
                      ? 'POI'
                      : Number(metrics?.mostUsedFeature) === KpiType.BOOKING
                        ? 'Booking'
                        : ''}
                </p>
                <div className="mt-2 text-sm text-gray-500">
                  Highlighting the top engagement area.
                </div>
              </div>
              {/* Active Users Card */}
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 md:col-span-2 lg:col-span-1">
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Active Users
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600">Daily</span>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatMetricCount(metrics?.activeUsers.daily)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Weekly</span>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatMetricCount(metrics?.activeUsers.weekly)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* show listing of all KPI logs below */}
          <div className="mt-8">
            <KpiLogsTable
              filters={{
                startDate: filters.startDate,
                endDate: filters.endDate,
                kpiType: filters.kpiType,
                role: filters.role,
              }}
            />
          </div>
        </div>
      </Container>
    </>
  );
};

export { KpiMetrics };
